import api from '../api';
import { Locales } from '@shared/enums/Locales';

/**
 * Create a clone of an event
 * @param { import("../api/types/Event").Event } event - The original event
 * @param { string } name - The name of the new event
 * @param { import("../api/types/Player").Player } [player] - The player participant for the event
 */
export async function cloneEvent(event, name, participantIds, categoryParentId, suppressErrorToast = false) {
	const {
		participants,
		sport,
		productPlacementEnabled = false,
		arenaDownloadEnabled = false,
		arenaCroppingEnabled = false,
		location = {},
		audioLanguages = null,
		geoblocking = null,
	} = event;

	const mappedParticipants = participants.map((e) => {
		const mapped = { ...e };
		mapped.participatableId = e.participatable.id;
		delete mapped.participatable;
		return mapped;
	});

	let currentParticipants = [];
	if (participantIds && Array.isArray(participantIds)) {
		// Specific player was supplied with the request to create a clip
		const participant = mappedParticipants.find((e) => participantIds.includes(e.participatableId));
		if (participant) currentParticipants.push(participant);
	} else {
		// Otherwise, attach all participants
		currentParticipants = mappedParticipants;
	}

	// Export clips with the current time (time of the export)
	const currentMoment = moment();

	// Event title is multi-language, get all name locales from the source event, fallback to window locale
	let nameLocales = Object.keys(event.nameTranslations).filter(l => event.nameTranslations[l]);
	if (nameLocales.length === 0) {
		nameLocales = [window.locale ?? Locales.DE];
	}
	const eventName = {};
	nameLocales.forEach(locale => eventName[locale] = name);

	// Other event settings to copy
	const additionalProps = {};
	if (location) {
		additionalProps.location = JSON.stringify(location);
	}
	if (productPlacementEnabled) {
		additionalProps.productPlacementEnabled = true;
	}
	if (arenaDownloadEnabled) {
		additionalProps.arenaDownloadEnabled = true;
	}
	if (arenaCroppingEnabled) {
		additionalProps.arenaCroppingEnabled = true;
	}
	if (audioLanguages?.length > 0) {
		additionalProps.audioLanguages = audioLanguages;
	}
	if (geoblocking?.whitelist?.length > 0 || geoblocking?.blacklist?.length > 0) {
		additionalProps.geoblocking = {
			whitelist: geoblocking.whitelist.join(','),
			blacklist: geoblocking.blacklist.join(','),
		};
	}

	// create the new event
	return await api.createEvent({
		name:             eventName,
		venueId:          null, // use no venue because venues sometimes have other dependencies (e.g. presets)
		categoryParentId: categoryParentId,
		participants:     JSON.stringify(currentParticipants),
		startDate:        currentMoment.format('DD.MM.YYYY'),
		startTime:        currentMoment.format('HH:mm'),
		visibility:       'hidden',
		isVod:            true,
		sportId:          sport.id,
		scoreLocked:      true,
		sourceEventId:    event.id,
		type:             'clip',
		...additionalProps,
	}, { suppressErrorToast });
};
